import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Trash2, Eye, MapPin, Phone, Calendar, FileImage, 
  User as UserIcon, CreditCard, Globe, Shield
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
  kyc_status: string;
  created_at: string;
  phone?: string;
  national_id?: string;
  gender?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  id_front_url?: string;
  id_back_url?: string;
  avatar_url?: string;
  language?: string;
  university_id?: string;
  budget_max?: number;
  roommates_count?: number;
  expected_move_in?: string;
  property_ownership_type?: string;
  payout_method?: string;
  updated_at?: string;
}

interface UserListViewProps {
  users: User[];
  onViewUser: (user: User) => void;
  onUpdateRole: (userId: string, newRole: string) => void;
  onDeleteUser: (userId: string) => void;
  isDeleting: boolean;
}

const UserListView = ({ users, onViewUser, onUpdateRole, onDeleteUser, isDeleting }: UserListViewProps) => {
  const { t } = useLanguage();

  const formatAddress = (user: User) => {
    const parts = [
      user.address_building,
      user.address_street,
      user.address_area,
      user.address_city,
      user.address_country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const viewDocument = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'landlord': return 'default';
      case 'student': return 'secondary';
      default: return 'outline';
    }
  };

  const getKYCBadgeVariant = (status: string) => {
    switch (status) {
      case 'VERIFIED': return 'default';
      case 'PENDING': return 'secondary';
      case 'REJECTED': return 'destructive';
      case 'UNDER_REVIEW': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-2">
      {users.map((user) => (
        <div key={user.id} className="bg-card border rounded-lg p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-center justify-between">
            {/* User Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <UserIcon className="h-5 w-5 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Name & Email */}
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm truncate">{user.full_name || 'Unnamed User'}</h3>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                
                {/* Contact */}
                <div className="text-xs space-y-1">
                  {user.phone && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      <span className="truncate">{user.phone}</span>
                    </div>
                  )}
                  {user.national_id && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <CreditCard className="h-3 w-3" />
                      <span>***{user.national_id.slice(-4)}</span>
                    </div>
                  )}
                </div>
                
                {/* Location & Info */}
                <div className="text-xs space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{formatAddress(user)}</span>
                  </div>
                  {user.language && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Globe className="h-3 w-3" />
                      <span className="capitalize">{user.language}</span>
                    </div>
                  )}
                </div>
                
                {/* Date & Status */}
                <div className="text-xs space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                  {(user.id_front_url || user.id_back_url) && (
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3 text-green-600" />
                      <span className="text-green-700 dark:text-green-300">KYC Docs</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Badges & Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex flex-col gap-1">
                <Badge variant={getRoleBadgeVariant(user.role)} className="text-xs px-2 py-0.5">
                  {user.role?.toUpperCase()}
                </Badge>
                <Badge variant={getKYCBadgeVariant(user.kyc_status)} className="text-xs px-2 py-0.5">
                  {user.kyc_status}
                </Badge>
              </div>
              
              <Select
                value={user.role}
                onValueChange={(newRole) => onUpdateRole(user.id, newRole)}
              >
                <SelectTrigger className="w-20 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">{t('student', 'طالب', 'Student')}</SelectItem>
                  <SelectItem value="landlord">{t('landlord', 'مالك', 'Landlord')}</SelectItem>
                  <SelectItem value="admin">{t('admin', 'مدير', 'Admin')}</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewUser(user)}
                  className="h-7 px-2"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 h-7 px-2"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('delete_user', 'حذف المستخدم', 'Delete User')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('delete_user_confirm', 
                          'هل أنت متأكد من حذف هذا المستخدم؟ هذا الإجراء لا يمكن التراجع عنه.',
                          `Are you sure you want to delete ${user.full_name}? This action cannot be undone and will permanently remove the user from both the website and database.`
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t('cancel', 'إلغاء', 'Cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDeleteUser(user.id)}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Deleting...' : t('delete', 'حذف', 'Delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      {users.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {t('no_users_found', 'لم يتم العثور على مستخدمين', 'No users found')}
        </div>
      )}
    </div>
  );
};

export default UserListView;